<template>
  <TheNavbar />

  <div class="mt-5 mb-5">

    <div class="row justify-content-center g-3">

      <!-- Single width-constraining column: the WHOLE page renders inside it, so every
           element shares the content width without per-element col classes. At xxl it
           spans the full container and the cards inside pair up as col-xxl-6. The inner
           row repeats g-3 so its gutter cancels against the outer one (Falcon's default
           row gutter is 2rem and would misalign nested cols by 3px per side). -->
      <div class="col-sm-12 col-md-10 col-lg-8 col-xl-8 col-xxl-12">
        <div class="row justify-content-center g-3">

          <!-- Access gate: hasStrategyAccess is the SAME stored flag the MCP uses to decide
              full vs limited (preview) Trend Radar responses, so this mirrors what the AI
              agent would actually get at runtime. It sits at the top of the page because
              it applies to ALL strategies below — AI and TradingView ones. -->
          <div v-if="!userHasAccessToStrategiesMix" class="col-12">
            <div class="alert alert-warning border-0 d-flex align-items-center mb-0" role="alert">
              <div class="bg-warning me-3 icon-item"><span class="fas fa-exclamation-circle text-white fs-6"></span></div>
              <p class="mb-0 flex-1">
                <span class="fw-bold">You don't have access to the SIGNUM Strategies anymore </span><br>
                Your access is free for the first 60 days after you've signed up or during your trial period, which is 14 days after the 1st trade you've made. Both have expired in your case.<br>
                <br>
                Subscribe now to regain access:<br>
                <router-link :to="{name: 'Subscription', params: {}}" class="btn btn-primary btn-sm mt-1" type="button">
                  Subscribe Now
                </router-link>
              </p>
            </div>
          </div>

          <!-- SIGNUM AI Strategies -->
          <div class="col-12 text-center">
            <h3>SIGNUM AI Strategies</h3>
            <p>Drive your Bots with an AI agent (like Claude) over the SIGNUM MCP. Select one of your Bots, pick a ready-made strategy prompt below and copy it - the prompt is personalized with your Bot's ID. Run it in your AI agent, or wrap it in a scheduled Routine to let it run automatically, hands-free.</p>
            <p class="mb-0">
              New to MCP? <a href="https://youtu.be/6w3vaAUT3RE" target="_blank" title="Watch the SIGNUM MCP walkthrough"><span class="fw-bold">How to use it</span><span class="fab fa-youtube fs-8" data-fa-transform="grow-4 right-5 down-1"></span></a>
            </p>
          </div>

          <!-- Prompt cards: each card has its own in-context Bot selector and handles the
               no-bots / all-paused / loading states itself; access gating stays up here. -->
          <template v-for="(p, idx) in orderedMcpPrompts" :key="p.id">
            <!-- "AutoTrading" section heading: rendered once, before the first AutoTrading-only prompt -->
            <div v-if="p.autoTradingOnly && (idx === 0 || !orderedMcpPrompts[idx - 1].autoTradingOnly)" class="col-12 text-center mt-5">
              <h3><span class="fas fa-crown text-warning" title="AutoTrading VIP only"></span> AutoTrading AI Strategies</h3>
              <p>These AI strategy prompts are only accessible for AutoTrading members.</p>
            </div>
            <div class="col-12 col-xxl-6" :class="userHasAccessToStrategiesMix ? 'opacity-100' : 'opacity-25'">
              <AiPromptCard
                :prompt="p"
                :bots="userBots || null"
                :disabled="!userHasAccessToStrategiesMix"
                disabled-reason="Subscribe to SIGNUM to use the AI strategy prompts." />
            </div>
          </template>

          <!-- SIGNUM TradingView Strategies -->
          <div class="col-12 text-center mt-5">
            <h3>SIGNUM TradingView Strategies</h3>
            <p>You have full access to these strategies, while you have an active SIGNUM account (active trial or active subscription). When your account becomes inactive, you automatically lose access to these strategies.</p>
          </div>

          <!-- The no-access warning alert for this section lives at the top of the page
              (it covers the AI Strategies section too — same hasStrategyAccess flag). -->

          <!-- SIGNUM Strategies (non-exclusive) -->
          <div v-for="strategy in signumStrategies" :key="strategy.name" class="col-12 col-xxl-6" :class="userHasAccessToStrategiesMix ? 'opacity-100' : 'opacity-25'">
            <div class="card h-100">
              <div class="card-header">
                <div class="row flex-between-end">
                  <div class="col-auto text-center">
                    <h5 class="mb-0" data-anchor="data-anchor">{{ strategy.tradingViewName }}</h5>
                  </div>
                </div>
              </div>
              <div class="card-body bg-body-tertiary">
                <img v-if="getStrategyImage(strategy.name)" class="img-fluid rounded mx-auto mb-3" :src="getStrategyImage(strategy.name)" />

                <p>
                  This strategy is private. You can get access to it by clicking on "Get Access" below.
                </p>

                <div class="text-center">
                  <a class="btn btn-primary btn-sm mt-1 mt-sm-0" type="button" :href="strategy.tradingViewUrl" target="_blank">
                    Open in TradingView <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span>
                  </a>
                  &nbsp;

                  <router-link v-if="userHasAccessToStrategiesMix" :to="{name: 'StrategyDetail', params: { strategyName: strategy.name }}" class="btn btn-primary btn-sm mt-1 mt-sm-0" type="button" href="">
                    Get Access
                  </router-link>
                  <a v-if="!userHasAccessToStrategiesMix" class="btn btn-primary btn-sm mt-1 mt-sm-0" type="button"> Get Access </a>
                </div>
              </div>
            </div> <!-- card-->
          </div> <!-- col-->

          <!-- ATX Strategies (exclusive for AutoTrading members) -->
          <div v-if="userHasAccessToAtxStrategiesMix && atxStrategies.length > 0" class="col-12 text-center mt-5">
            <h3><span class="fas fa-crown text-warning" title="AutoTrading VIP only"></span> AutoTrading Extra TradingView Strategies</h3>
            <p>These strategies are only accessible for AutoTrading members. Those are variants Michael has coded but not deemed "better" than what is already in the AutoTrading Masterclass. You'll also find strategies made from famous and well known indicators. Enjoy </p>
          </div>

          <template v-if="userHasAccessToAtxStrategiesMix">
            <div v-for="strategy in atxStrategies" :key="strategy.name" class="col-12 col-xxl-6">
              <div class="card h-100">
                <div class="card-header">
                  <div class="row flex-between-end">
                    <div class="col-auto text-center">
                      <h5 class="mb-0" data-anchor="data-anchor">{{ strategy.tradingViewName }}</h5>
                    </div>
                  </div>
                </div>
                <div class="card-body bg-body-tertiary">
                  <img v-if="getStrategyImage(strategy.name)" class="img-fluid rounded mx-auto mb-3" :src="getStrategyImage(strategy.name)" />

                  <p>
                    This strategy is private. You can get access to it by clicking on "Get Access" below.
                  </p>

                  <div class="text-center">
                    <a class="btn btn-primary btn-sm mt-1 mt-sm-0" type="button" :href="strategy.tradingViewUrl" target="_blank">
                      Open in TradingView <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span>
                    </a>
                    &nbsp;

                    <router-link :to="{name: 'StrategyDetail', params: { strategyName: strategy.name }}" class="btn btn-primary btn-sm mt-1 mt-sm-0" type="button" href="">
                      Get Access
                    </router-link>
                  </div>

                </div>
              </div> <!-- card-->
            </div> <!-- col-->
          </template>
          
          <div class="col-12 text-center mt-5">
            <h3>Help & Education</h3>
          </div>

          <div class="col-12 col-xxl-6">
            <div class="card">
              <div class="card-header">
                <div class="row flex-between-end">
                  <div class="col-auto">
                    <h5 class="mb-0" data-anchor="data-anchor">How to use these TradingView strategies?</h5>
                  </div>
                </div>
              </div>
              <div class="card-body bg-body-tertiary">
                <img class="img-fluid rounded" style="max-height: 40px;" src="@/assets/strategies/open_tv_strategy.jpg" />
                <p>
                  Open the strategy you want to use.
                </p>

                <img class="img-fluid rounded" style="max-height: 40px;" src="@/assets/strategies/add_to_fav.jpg" />
                <p>
                  Scroll down and add the strategy to your favorites.
                </p>

                <img class="img-fluid rounded mx-auto" src="@/assets/strategies/tv_indicators.jpg" />
                <p>
                  <a href="https://www.tradingview.com/chart/" target="_blank">Go to TradingView <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> and open the <span class="fw-bold">Indicators</span> menu.<br>
                  Click on the strategies you want to add to your chart (click once per strategy).<br>
                  <br>
                  Now go to the <router-link :to="{name: 'MyBots', params: {}}" class="" href="">SIGNUM Bot</router-link> that you want to use (or create one) and follow the instructions there :)
                </p>


              </div>
            </div> <!-- card-->
          </div> <!-- col-->

          <div class="col-12 col-xxl-6">
            <div class="card">
              <div class="card-header">
                <div class="row flex-between-end">
                  <div class="col-auto">
                    <h5 class="mb-0" data-anchor="data-anchor">AutoTrading Masterclass</h5>
                  </div>
                </div>
              </div>
              <div class="card-body bg-body-tertiary">
                <img class="img-fluid rounded mx-auto mb-3" src="@/assets/strategies/at_course_cover.jpg" />

                <p>
                  Learn how to automate your Bitcoin & Crypto investments with algo trading strategies. Become a master in risk & portfolio management and trade automation.<br>
                  Get access to the AutoTrading trading strategies, the community and 12 months free SIGNUM access.
                </p>

                <div class="text-center">
                  <a class="btn btn-primary btn-sm" type="button" href="https://autotrading.vip" target="_blank">
                    Learn More <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span>
                  </a>
                </div>
              </div>
            </div> <!-- card-->
          </div> <!-- col-->

        </div> <!-- inner row -->
      </div> <!-- width-constraining col -->

    </div>

  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar'
import AiPromptCard from '@/components/AiPromptCard'
import { userHelperMixin } from '@/mixins/userHelperMixin'
import { mcpPrompts } from '@/utils/mcpPrompts'

export default {
  mixins: [userHelperMixin],
  components: {
    TheNavbar,
    AiPromptCard
  },
  computed: {
    userBots () {
      return this.$store.getters.getUserBots
    },
    visibleMcpPrompts () {
      const isAutoTradingUser = this.userProfileMix?.isAutotradingUser
      const userId = this.userProfileMix?.id
      return mcpPrompts.filter(p =>
        (!p.hidden || p.previewUserIds?.includes(userId)) &&
        (!p.autoTradingOnly || isAutoTradingUser))
    },
    orderedMcpPrompts () {
      // Public (non-AutoTrading) prompts first, then AutoTrading-only ones, so the
      // "AutoTrading" heading can be injected at the boundary. Array.sort is stable,
      // so the original mcpPrompts order is preserved within each group.
      return [...this.visibleMcpPrompts].sort((a, b) =>
        (a.autoTradingOnly ? 1 : 0) - (b.autoTradingOnly ? 1 : 0))
    },
    strategies () {
      const strategies = this.$store.getters.getStrategies
      
      // Avoiding race condition when re-loading the page
      if (strategies)
        return Object.values(strategies).filter(s => s.name != 'GAUSSIAN_CHANNEL_PRO') // Decided to hide GAUSSIAN_CHANNEL_PRO
      
      return null
    },
    signumStrategies () {
      if (!this.strategies) return []
      return Object.values(this.strategies).filter(s => !s.isExclusiveForAutoTrading)
    },
    atxStrategies () {
      if (!this.strategies) return []
      return Object.values(this.strategies).filter(s => s.isExclusiveForAutoTrading)
    },
    getSupportUrl () {
      return process.env.VUE_APP_SUPPORT_URL
    }
  },
  methods: {
    getStrategyImage (strategyName) {
      try {
        return require('@/assets/strategies/' + strategyName + '.png')
      } catch (e) {
        return null
      }
    }
  },
  async created () {
    if (!this.$store.getters.getUserBots) await this.$store.dispatch('getUserBots')
    if (!this.$store.getters.getStrategies) await this.$store.dispatch('getStrategies')
  },
  beforeMount () {
    // Redirect to Login when the user is not logged in
    if (!this.$store.getters.isUserLoggedIn) this.$router.push({ name: 'Login' })
  }

}
</script>
